import React from "react";
import { useTranslation } from "react-i18next";
import { Typography, Box, Stack } from "@mui/material";
import { ResponsiveModal } from "@answersnow/pkg-theme";
import { DateCalendar } from "@mui/x-date-pickers";
import { PickersDay } from "@mui/x-date-pickers/PickersDay";
import { Appointment } from "api/types/appointments";
import NextAppointmentCard from "./NextAppointmentCard";
import { getCallsByDate } from "../utils";

interface ViewAllAppointmentsProps {
  calls: Appointment[];
}

const ViewAllAppointments: React.FC<ViewAllAppointmentsProps> = ({ calls }) => {
  const { t } = useTranslation(["dashboard"]);
  const [open, setOpen] = React.useState(false);
  const [selectedDate, setSelectedDate] = React.useState(new Date());

  const handleOpen = () => {
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
  };

  const handleDateChange = (value: Date | null) => {
    if (value) {
      setSelectedDate(value);
    }
  };

  const callsForSelectedDate = getCallsByDate(calls, selectedDate);

  const getDaysWithCalls = () => {
    const daysWithCalls = new Set();
    calls.forEach((call) => {
      if (call.scheduled_date) {
        const callDate = new Date(call.scheduled_date);
        daysWithCalls.add(callDate.toDateString());
      }
    });
    return daysWithCalls;
  };

  const daysWithCalls = getDaysWithCalls();

  const CustomDay = (props: any) => {
    const { day, outsideCurrentMonth, ...other } = props;
    const hasCall = daysWithCalls.has(day.toDateString());

    return (
      <Box sx={{ position: "relative" }}>
        <PickersDay
          {...other}
          day={day}
          outsideCurrentMonth={false}
          sx={{
            ...(outsideCurrentMonth && {
              color: "grey.400",
              opacity: 0.6,
            }),
          }}
        />
        {hasCall && (
          <Box
            sx={{
              position: "absolute",
              bottom: "2px",
              left: "50%",
              transform: "translateX(-50%)",
              width: "6px",
              height: "6px",
              borderRadius: "100px",
              background: "#8E42CA",
            }}
          />
        )}
      </Box>
    );
  };

  return (
    <>
      <Typography variant="body2" component="span" onClick={handleOpen} sx={{ cursor: "pointer" }}>
        {t("dashboard:parent.nextAppointment.viewAll")}
      </Typography>

      <ResponsiveModal
        open={open}
        onClose={handleClose}
        onOpen={handleOpen}
        title={t("dashboard:parent.nextAppointment.selectDate")}
        maxWidth="xs"
        drawerSx={
          callsForSelectedDate.length > 0
            ? {
                maxHeight: "85vh",
              }
            : {}
        }
        drawerContentSx={
          callsForSelectedDate.length > 0
            ? {
                maxHeight: "60vh",
              }
            : {}
        }
        dialogSx={{ maxHeight: "60vh" }}
      >
        <DateCalendar
          value={selectedDate}
          disablePast
          onChange={handleDateChange}
          slots={{
            day: CustomDay,
          }}
          sx={{
            "& .MuiPickersDay-root.Mui-selected": {
              backgroundColor: "#8E42CA",
              "&:hover": {
                backgroundColor: "#8E42CA",
              },
              "&:focus": {
                backgroundColor: "#8E42CA",
              },
            },
            "& .MuiPickersDay-root.Mui-selected:not(.MuiPickersDay-dayOutsideMonth)": {
              backgroundColor: "#8E42CA",
            },
            "& .MuiPickersDay-dayOutsideMonth": {
              color: "grey.400",
              opacity: 0.6,
            },
            "& .MuiPickersDay-dayOutsideMonth.Mui-selected": {
              backgroundColor: "#8E42CA",
              color: "white",
              opacity: 1,
              "&:focus": {
                backgroundColor: "#8E42CA !important",
              },
            },
            "& .MuiButtonBase-root.MuiPickersDay-root:focus": {
              backgroundColor: "transparent",
            },
            "& .MuiButtonBase-root.MuiPickersDay-root.Mui-selected:focus": {
              backgroundColor: "#8E42CA !important",
            },
          }}
        />
        <Stack
          direction={"column"}
          gap={5}
          sx={{
            width: "100%",
            alignItems: "center",
            paddingTop: 5,
            paddingBottom: 5,
          }}
        >
          {callsForSelectedDate.map((appointment) => (
            <NextAppointmentCard
              key={appointment.id}
              appointmentData={appointment}
              showJoinButton={false}
            />
          ))}
        </Stack>
      </ResponsiveModal>
    </>
  );
};

export default ViewAllAppointments;
